import type { MouseEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth as useAuthContext } from '@/components/auth/AuthProvider'
import { getCollectionIcon } from '@/components/shared/custom-fields/CollectionIconPicker'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { type NavItem } from '@/config/navigation'
import { useUnreadThreadsCount } from '@/hooks/queries/useThreadsQuery'
import { useUnreadTicketsCount } from '@/hooks/queries/useTicketUnreadCount'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAccountType } from '@/hooks/useAccountType'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { usePermissions } from '@/hooks/usePermissions'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { customCollectionsApi } from '@/lib/api/custom-collections.api'
import { createLogger } from '@/lib/logger'
import { prefetchModuleForPath } from '@/lib/prefetch/modulePrefetch'
import { useThreadListRealtime } from '@/lib/realtime/features/useThreadListRealtime'
import { cn } from '@/lib/utils'
import {
  isPopupWindow,
  isTauri,
  openFocusWindow,
  openInNewWindow,
} from '@/lib/utils/tauri'
import { useOrganizationStore } from '@/store/organization.store'
import {
  SIDEBAR_THEMES,
  useUIPreferencesStore,
} from '@/store/ui-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { getOrgPath } from '@/utils/org-paths'
import {
  ArrowLeftIcon,
  ArrowSquareOutIcon,
  BookOpenTextIcon,
  CaretDownIcon,
  CaretRightIcon,
  DotsThreeIcon,
  EyeIcon,
  EyeSlashIcon,
  GearIcon,
  SidebarSimpleIcon,
  UserPlusIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import type { FeatureFlagKeyType, SectionKey } from '@scrambl/shared'
import {
  ModuleTypeEnum,
  PermissionActionEnum,
  ThreadTypeEnum,
} from '@scrambl/shared'

import { Header } from './Header'
import { InviteTeamMembersDialog } from './InviteTeamMembersDialog'

const logger = createLogger('components/layout/AppSidebar')

const SUBSCRIPTION_NAV_BADGES: Record<string, string> = {
  analytics: 'Business',
}

interface AppSidebarProps {
  children: ReactNode
}

// Optimized sidebar trigger with reduced animation
function CustomSidebarTrigger({ className }: { className?: string }) {
  const { toggleSidebar, state } = useSidebar()
  const setSidebarOpen = useUIPreferencesStore(state => state.setSidebarOpen)

  const handleToggle = () => {
    toggleSidebar()
    // Direct state update without timeout
    setSidebarOpen(state === 'collapsed')
  }

  return (
    <Button
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7', className)}
      onClick={handleToggle}
    >
      <SidebarSimpleIcon weight="bold" />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
}

export function AppSidebar({ children }: AppSidebarProps) {
  const location = useLocation()
  const { user } = useAuthContext()
  const { state, setOpenMobile } = useSidebar()
  const { checkPermission, isLoadingPermissions } = usePermissions()
  const setSectionCollapsed = useUIPreferencesStore(
    state => state.setSectionCollapsed,
  )
  const collapsedSections = useUIPreferencesStore(
    state => state.collapsedSections,
  )
  const isAdminMode = useUIPreferencesStore(state => state.isAdminMode)
  const setAdminMode = useUIPreferencesStore(state => state.setAdminMode)
  const sidebarTheme = useUIPreferencesStore(state => state.sidebarTheme)
  const tauriGlassEnabled = useUIPreferencesStore(
    state => state.tauriGlassEnabled,
  )
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  // Compute admin status directly from organization to ensure reactivity
  const membershipRole = currentOrganization?.userMembership?.role
  const isExternalAccess = currentOrganization?.isExternalAccess === true
  const preloadProfileImages = useUserProfileStore(
    state => state.preloadProfileImages,
  )
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [expandedMoreSections, setExpandedMoreSections] = useState<Set<string>>(
    new Set(),
  )
  const { theme } = useTheme()
  const organizationId = currentOrganization?.id || null
  const { checkFeature, isLoading: isLoadingSubscriptionFeatures } =
    useSubscriptionFeatures()

  // Get pinned collections for sidebar (must be before isLinkActive which uses it)
  const { preferences: navPreferences, setNavItemHidden } =
    useNavigationPreferences()

  // Optimistic per-user nav visibility overrides. Keyed by item href. This
  // mirrors what gets persisted to the navigation preference, but updating it
  // is a local state change so the sidebar re-renders the show/hide split
  // immediately on click — without waiting for the save round-trip or relying
  // on the derived-preference re-render to propagate.
  const [pendingNavVisibility, setPendingNavVisibility] = useState<
    Record<string, boolean>
  >({})

  // Effective visibility map: persisted preference values with any optimistic
  // (just-clicked) overrides layered on top.
  const effectiveHiddenNavItems = useMemo(
    () => ({
      ...(navPreferences.hiddenNavItems ?? {}),
      ...pendingNavVisibility,
    }),
    [navPreferences.hiddenNavItems, pendingNavVisibility],
  )

  // Resolve effective visibility for a nav item: an override (optimistic or
  // persisted) wins, otherwise the item's built-in default applies.
  const isNavItemHidden = useCallback(
    (href: string, defaultHidden = false): boolean => {
      const override = effectiveHiddenNavItems[href]
      return typeof override === 'boolean' ? override : defaultHidden
    },
    [effectiveHiddenNavItems],
  )

  const hasCollectionsFeature = checkFeature('collections')
  const pinnedCollectionAccessQueries = useQueries({
    queries: (navPreferences.pinnedCollections ?? []).map(collection => ({
      queryKey: [
        'custom-collections',
        'sidebar-access',
        organizationId,
        collection.collectionId,
      ],
      queryFn: () =>
        customCollectionsApi.getCollection(collection.collectionId),
      enabled: !!organizationId && hasCollectionsFeature,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  })
  const accessibleCollectionIds = new Set(
    pinnedCollectionAccessQueries
      .filter(query => query.isSuccess)
      .map(query => query.data.id),
  )
  const accessiblePinnedCollections = (() => {
    if (!navPreferences.pinnedCollections?.length) return []
    return navPreferences.pinnedCollections.filter(item =>
      accessibleCollectionIds.has(item.collectionId),
    )
  })()

  useThreadListRealtime(organizationId, { suppressLogging: true })

  // Determine if we're in dark mode (for system theme, check the actual applied theme)
  const isDarkMode = (() => {
    if (theme === 'dark') return true
    if (theme === 'light') return false
    // For system theme, check what's actually applied
    return (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    )
  })()

  // Get the current theme configuration
  const themeConfig =
    SIDEBAR_THEMES.find(t => t.id === sidebarTheme) || SIDEBAR_THEMES[0]

  // In dark mode, all themes use light text (dark backgrounds)
  // In light mode, use the theme's specified light text color
  const useDarkText = isDarkMode ? false : themeConfig.lightTextColor === 'dark'

  // Memoize expensive calculations - include all relevant state to ensure it updates when org changes
  const hasAdminPermissions = (() => {
    // Return false if no organization is set to prevent premature admin checks
    if (!currentOrganization) {
      return false
    }
    // External org users cannot manage the org (they use the host's permission set for other features)
    if (isExternalAccess) {
      return false
    }
    // Check if user is admin/owner via membership
    return membershipRole === 'ADMIN' || membershipRole === 'OWNER'
  })()
  const isIntegrationsAdminRoute = useMemo(
    () => location.pathname.includes('/admin/integrations'),
    [location.pathname],
  )
  const canAccessIntegrationsAdminRoute =
    isIntegrationsAdminRoute &&
    (isLoadingPermissions ||
      checkPermission(ModuleTypeEnum.INTEGRATION, PermissionActionEnum.READ))
  const hasAdminModeAccess =
    hasAdminPermissions || canAccessIntegrationsAdminRoute
  const isSidebarCollapsed = state === 'collapsed'

  // Monitor organization context changes - triggers re-render when org changes
  useEffect(() => {
    // Effect triggers when org or admin permissions change
  }, [currentOrganization, hasAdminPermissions])

  // Auto-exit admin mode if user loses admin permissions
  useEffect(() => {
    if (isAdminMode && !hasAdminModeAccess && currentOrganization) {
      setAdminMode(false)
      navigate(`/${currentOrganization.slug}/today`)
    }
  }, [
    isAdminMode,
    hasAdminModeAccess,
    setAdminMode,
    navigate,
    currentOrganization,
  ])

  // Simplified profile image loading - use preload method
  useEffect(() => {
    if (user?.id) {
      const timer = setTimeout(() => {
        preloadProfileImages([user.id])
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [user?.id, preloadProfileImages])

  // Memoized callback functions
  const toggleSection = useCallback(
    (section: SectionKey) => {
      // Use the subscribed state to get current collapsed state
      const currentCollapsedState = collapsedSections[section]
      setSectionCollapsed(section, !currentCollapsedState)
    },
    [setSectionCollapsed, collapsedSections],
  )

  const handleNavigate = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const handleCloseMobile = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const toggleAdminMode = () => {
    if (!hasAdminPermissions) {
      logger.warn('User does not have admin permissions')
      return
    }

    if (isMobile) {
      setOpenMobile(false)
    }

    const newAdminMode = !isAdminMode
    setAdminMode(newAdminMode)

    if (newAdminMode) {
      if (currentOrganization?.slug) {
        navigate(`/${currentOrganization.slug}/admin/settings`)
      }
    } else {
      if (currentOrganization?.slug) {
        navigate(`/${currentOrganization.slug}/today`)
      } else {
        navigate('/today')
      }
    }
  }

  // Memoized section collapse state
  const isSectionCollapsed = useCallback(
    (sectionKey: SectionKey) => {
      if (isMobile || isSidebarCollapsed) return false
      // Use the subscribed state so component re-renders when it changes
      return collapsedSections[sectionKey]
    },
    [isMobile, isSidebarCollapsed, collapsedSections],
  )

  // Memoized link active state
  const isLinkActive = useCallback(
    (path: string) => {
      if (path.includes('/admin/')) {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}${path}` ||
            location.pathname.endsWith(path)
          )
        }
        return location.pathname.endsWith(path)
      }

      if (path === '/help') {
        return (
          location.pathname === '/help' ||
          location.pathname.startsWith('/help/')
        )
      }

      if (path === '/today') {
        if (currentOrganization?.slug) {
          return (
            location.pathname === `/${currentOrganization.slug}/today` ||
            location.pathname === `/${currentOrganization.slug}`
          )
        }
        return location.pathname === '/today'
      }

      // My Work and Workspace Tasks are separate navigation contexts even
      // though they share the underlying task module and route tree.
      if (path === '/tasks/mine') {
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, path)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === path || location.pathname.startsWith(`${path}/`)
        )
      }

      if (path === '/tasks') {
        const orgPath = currentOrganization?.slug
          ? getOrgPath(currentOrganization.slug, path)
          : path
        const minePath = `${orgPath}/mine`
        return (
          (location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)) &&
          location.pathname !== minePath &&
          !location.pathname.startsWith(`${minePath}/`)
        )
      }

      // Notes still uses personal/workspace lenses inside one shared module.
      if (path === '/notes/mine') {
        const modulePath = '/notes'
        if (currentOrganization?.slug) {
          const orgPath = getOrgPath(currentOrganization.slug, modulePath)
          return (
            location.pathname === orgPath ||
            location.pathname.startsWith(`${orgPath}/`)
          )
        }
        return (
          location.pathname === modulePath ||
          location.pathname.startsWith(`${modulePath}/`)
        )
      }

      // For the generic /collections path, don't highlight if we're on a pinned collection
      if (path === '/collections') {
        const pinnedSlugs = accessiblePinnedCollections.map(c => c.slug)
        const currentPathSegments = location.pathname.split('/')
        // Check if current path is /org/collections/slug where slug is a pinned collection
        if (
          currentPathSegments.length >= 4 &&
          currentPathSegments[2] === 'collections'
        ) {
          const currentSlug = currentPathSegments[3]
          if (pinnedSlugs.includes(currentSlug)) {
            return false // Don't highlight Collections when on a pinned collection page
          }
        }
      }

      if (currentOrganization?.slug) {
        const orgPath = getOrgPath(currentOrganization.slug, path)
        return (
          location.pathname === orgPath ||
          location.pathname.startsWith(`${orgPath}/`)
        )
      }

      return (
        location.pathname === path ||
        (path !== '/' && location.pathname.startsWith(path))
      )
    },
    [location.pathname, currentOrganization, accessiblePinnedCollections],
  )

  // Memoized navigation path
  const getNavigationPath = useCallback(
    (path: string) => {
      const globalRoutes = ['/organizations', '/profile', '/account', '/help']

      // Check exact matches for global routes
      if (globalRoutes.includes(path)) {
        return path
      }

      // User settings routes are global (not org-specific), except integrations which are org-scoped
      if (
        path.startsWith('/settings') &&
        !path.startsWith('/settings/integrations')
      ) {
        return path
      }

      if (path.startsWith('/admin/')) {
        if (currentOrganization?.slug) {
          return `/${currentOrganization.slug}${path}`
        }
        return path
      }

      if (currentOrganization?.slug) {
        return getOrgPath(currentOrganization.slug, path)
      }

      if (path === '/today') {
        return '/today'
      }

      return path
    },
    [currentOrganization],
  )

  // Get navigation items
  const { mainNavItems, adminNavItems, sections } = useNavigationItems()
  const featureKeys = useMemo(
    () =>
      mainNavItems
        .map(item => item.featureFlag)
        .filter(Boolean) as FeatureFlagKeyType[],
    [mainNavItems],
  )
  const { checkFeatureFlag } = useFeatureFlags(featureKeys)
  const { accountType, isPersonal } = useAccountType()

  // Determine if we should show loading skeleton for navigation
  // Show skeleton when:
  // 1. Organization exists but userMembership hasn't loaded yet (and it's not external access), OR
  // 2. User is not admin/owner AND permissions are still loading
  // Note: membershipRole and isExternalAccess are already defined at the top of the component
  const isAdminOrOwner =
    membershipRole === 'OWNER' || membershipRole === 'ADMIN'
  // For external org access, we don't have userMembership - permissions come from the external org relationship
  // So we skip the userMembership check for external access
  const isNavLoading =
    (currentOrganization?.id &&
      !currentOrganization?.userMembership?.id &&
      !isExternalAccess) ||
    (!isAdminOrOwner && !isExternalAccess && isLoadingPermissions)

  // Get unread threads count for badge
  const { data: unreadThreadsCount } = useUnreadThreadsCount({
    threadType: ThreadTypeEnum.DISCUSSION,
  })

  // Unread tickets count for the Tickets nav badge
  const { data: unreadTicketsCount } = useUnreadTicketsCount()

  // Check if we should show focus window buttons (desktop only, not in popup mode)
  const showFocusWindowButton = isTauri() && !isPopupWindow() && !isMobile
  const isDesktopTauri = isTauri() && !isMobile

  // Handle opening a nav item in a focus window
  const handleOpenFocusWindow = useCallback(
    async (e: MouseEvent, path: string, label: string) => {
      e.preventDefault()
      e.stopPropagation()
      const fullPath = getNavigationPath(path)
      await openFocusWindow(fullPath, label)
    },
    [getNavigationPath],
  )

  const handleOpenHelpCenter = useCallback(
    async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (isMobile) {
        setOpenMobile(false)
      }

      if (isTauri() && !isMobile) {
        await openInNewWindow('/help', 'Help Center')
        return
      }

      navigate('/help')
    },
    [isMobile, navigate, setOpenMobile],
  )

  // Warm the route's JS chunk on hover intent. Pure dynamic import — no API
  // traffic — so it's safe on web too, not just desktop.
  const handlePrefetch = (path: string) => {
    if (isPopupWindow()) return
    void prefetchModuleForPath(path)
  }

  // Toggle a single nav item between "always visible" and "under More" for the
  // current user. Updates optimistic local state for an instant UI change, then
  // persists to the org-scoped USER navigation preference. Reverts on failure.
  const handleToggleNavVisibility = useCallback(
    (e: MouseEvent, href: string, nextHidden: boolean) => {
      e.preventDefault()
      e.stopPropagation()
      setPendingNavVisibility(prev => ({ ...prev, [href]: nextHidden }))
      void setNavItemHidden(href, nextHidden).catch(error => {
        logger.error('Failed to save nav visibility, reverting', error)
        setPendingNavVisibility(prev => ({ ...prev, [href]: !nextHidden }))
      })
    },
    [setNavItemHidden],
  )

  // Memoized filtered navigation items
  const filteredNavItems = (() => {
    return mainNavItems.filter(item => {
      // Account-type gate for surfaces feature flags can't express. Absent =
      // visible to all account types.
      if (item.accountTypes && !item.accountTypes.includes(accountType)) {
        return false
      }

      // Feature flag is the org-level toggle and matches what the route guard checks.
      // If an admin explicitly enables the flag, the route is already reachable, so the
      // sidebar should show the item too — even if the subscription plan technically
      // doesn't include it. Enforce the subscription gate only when no feature flag is
      // configured on the nav item, so plan-only items still respect their tier.
      if (item.featureFlag) {
        if (!checkFeatureFlag(item.featureFlag)) {
          return false
        }
      } else if (
        item.subscriptionFeature &&
        !checkFeature(item.subscriptionFeature)
      ) {
        return false
      }

      if (
        item.permission &&
        !checkPermission(item.permission.module, item.permission.action)
      ) {
        return false
      }

      return true
    })
  })()

  // Memoized grouped navigation items
  const groupedNavItems = (() => {
    return filteredNavItems.reduce<
      Record<SectionKey, { title?: string; items: NavItem[] }>
    >(
      (acc, item) => {
        if (!item.section) return acc
        const section = sections[item.section]
        if (!section) return acc

        if (!acc[item.section]) {
          acc[item.section] = {
            title: section.title,
            items: [],
          }
        }
        acc[item.section].items.push(item)
        return acc
      },
      {} as Record<SectionKey, { title?: string; items: NavItem[] }>,
    )
  })()

  // Get pinned collections for a specific section
  const getPinnedCollectionsForSection = useCallback(
    (sectionKey: SectionKey) => {
      return accessiblePinnedCollections.filter(
        item => item.section === sectionKey,
      )
    },
    [accessiblePinnedCollections],
  )

  // Dynamic text color classes based on theme
  const textClasses = useDarkText
    ? 'text-black/80 hover:text-black'
    : 'text-white/80 hover:text-white'
  const textMutedClasses = useDarkText ? 'text-black/60' : 'text-white/60'
  // Section labels use the .section-header utility for typography; its
  // muted-foreground color and bottom margin are overridden (via important
  // modifiers, since the utility compiles after core utilities) so the
  // theme-aware sidebar text colors still apply.
  const sectionLabelColorClasses = useDarkText
    ? '!text-black/60'
    : '!text-white/60'
  const sectionLabelHoverClasses = useDarkText
    ? 'hover:!text-black/80'
    : 'hover:!text-white/80'
  const hoverBgClasses = useDarkText ? 'hover:bg-black/10' : 'hover:bg-white/10'
  const activeBgClasses = useDarkText
    ? 'data-[active=true]:bg-black/15 data-[active=true]:text-black'
    : 'data-[active=true]:bg-white/15 data-[active=true]:text-white'
  const borderClasses = useDarkText ? 'border-black/10' : 'border-white/5'

  return (
    <div
      className="app-shell-frame flex h-full min-w-0 flex-1"
      data-sidebar-collapsed={isSidebarCollapsed ? 'true' : 'false'}
    >
      <Sidebar
        variant="sidebar"
        collapsible={useIsMobile() ? 'offcanvas' : 'icon'}
        tauriOffset={isTauri() && !isMobile}
        className={cn(
          'themed-sidebar enhanced-sidebar app-shell-sidebar bg-sidebar transition-colors duration-300',
          isDesktopTauri && 'tauri-desktop-sidebar',
          isDesktopTauri && tauriGlassEnabled && 'tauri-glass-sidebar',
          isMobile
            ? '!sm:p-0 !md:p-0 border-r-0 !p-0'
            : isDesktopTauri
              ? 'border-r-0'
              : 'border-r',
          borderClasses,
        )}
      >
        {/* Header section - Logo for web, close button for mobile */}
        {isMobile ? (
          <div className="bg-sidebar flex h-14 items-center justify-between px-4 transition-colors duration-300">
            <Link
              to="/organizations"
              className="hover:opacity-80"
              onClick={handleNavigate}
            >
              <span
                className={cn(
                  'font-kronaone text-lg font-normal tracking-wide',
                  useDarkText ? 'text-black/85' : 'text-white/85',
                )}
              >
                scrambl
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseMobile}
              className={cn('h-8 w-8', textClasses, hoverBgClasses)}
            >
              <XIcon className="h-5 w-5" weight="bold" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
        ) : (
          !isTauri() && (
            <div
              className={cn(
                'bg-sidebar flex h-14 items-center justify-center px-3 transition-colors duration-300',
              )}
            >
              <Link
                to="/organizations"
                className="hover:opacity-80"
                onClick={handleNavigate}
              >
                {isSidebarCollapsed ? (
                  <img
                    src="/logo-outline.svg"
                    alt="Scrambl"
                    className="h-8 w-8"
                    style={{
                      filter: useDarkText
                        ? 'invert(1) opacity(0.8)'
                        : 'opacity(0.8)',
                    }}
                  />
                ) : (
                  <span
                    className={cn(
                      'font-kronaone text-lg font-normal tracking-wide',
                      useDarkText ? 'text-black/85' : 'text-white/85',
                    )}
                  >
                    scrambl
                  </span>
                )}
              </Link>
            </div>
          )
        )}

        <SidebarContent
          className={cn(
            'bg-sidebar min-h-0 flex-1 transition-colors duration-300',
            isMobile ? '!p-0' : '',
          )}
        >
          <div className="sidebar-scrollbar h-full overflow-y-auto">
            <SidebarMenu
              className={cn(
                'space-y-1',
                // Adjust padding for mobile vs desktop
                isMobile
                  ? 'px-4 pb-6 pt-2'
                  : 'pb-4 pt-1 group-data-[state=collapsed]:px-0 group-data-[state=expanded]:px-2',
              )}
            >
              {/* Back to App button for admin mode */}
              {isAdminMode && (
                <div className={cn('mb-3 border-b pb-2', borderClasses)}>
                  <div className="space-y-0.5">
                    <SidebarMenuItem className="flex justify-center group-data-[state=collapsed]:mb-0">
                      <SidebarMenuButton
                        tooltip="Back to App"
                        size="sm"
                        onClick={toggleAdminMode}
                        className={cn(
                          'h-8 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                          textClasses,
                          hoverBgClasses,
                          isMobile && 'w-full px-4',
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <ArrowLeftIcon className="h-5 w-5" />
                          <span className="text-sm font-[450] group-data-[state=collapsed]:hidden">
                            Back to App
                          </span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </div>
                </div>
              )}

              {/* Navigation sections */}
              {isAdminMode ? (
                <div>
                  <div className="hidden group-data-[state=expanded]:block group-data-[state=expanded]:px-1">
                    <h2
                      className={cn(
                        'section-header !mb-0 px-2.5 py-0.5',
                        sectionLabelColorClasses,
                      )}
                    >
                      Admin Panel
                    </h2>
                  </div>
                  <SidebarSeparator
                    className={cn(
                      'hidden opacity-10 group-data-[state=collapsed]:my-0.5',
                      useDarkText ? 'bg-black/5' : 'bg-white/3',
                    )}
                  />
                  <div className="space-y-0.5">
                    {adminNavItems
                      .filter(
                        item =>
                          !item.accountTypes ||
                          item.accountTypes.includes(accountType),
                      )
                      .map(item => (
                        <SidebarMenuItem
                          key={item.href}
                          className="flex justify-center group-data-[state=collapsed]:mb-0"
                        >
                          <SidebarMenuButton
                            asChild
                            isActive={isLinkActive(item.href)}
                            tooltip={item.label}
                            size="sm"
                            className={cn(
                              'h-8 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                              textClasses,
                              hoverBgClasses,
                              activeBgClasses,
                              isMobile && 'w-full px-4',
                            )}
                          >
                            <Link
                              to={getNavigationPath(item.href)}
                              className="flex items-center gap-2"
                              onClick={handleNavigate}
                              onMouseEnter={() => handlePrefetch(item.href)}
                              onFocus={() => handlePrefetch(item.href)}
                            >
                              <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                                <item.icon className="h-5 w-5" />
                              </div>
                              <span
                                className={cn(
                                  'text-sm group-data-[state=collapsed]:hidden',
                                  isLinkActive(item.href)
                                    ? 'font-[550]'
                                    : 'font-[450]',
                                )}
                              >
                                {item.label}
                              </span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                  </div>
                </div>
              ) : isNavLoading ? (
                // Loading skeleton for navigation items
                <div className="space-y-4">
                  {/* Section 1 skeleton */}
                  <div>
                    <div
                      className={cn(
                        'mb-2',
                        isMobile
                          ? 'px-0'
                          : 'group-data-[state=collapsed]:hidden group-data-[state=expanded]:px-1',
                      )}
                    >
                      <Skeleton
                        className={cn(
                          'h-4 w-12 rounded',
                          useDarkText ? 'bg-black/10' : 'bg-white/10',
                        )}
                      />
                    </div>
                    <div className="space-y-1">
                      {[1, 2, 3, 4].map(i => (
                        <div
                          key={i}
                          className={cn(
                            'flex h-8 items-center gap-2',
                            isMobile
                              ? 'px-4'
                              : 'group-data-[state=collapsed]:justify-center group-data-[state=expanded]:px-4',
                          )}
                        >
                          <Skeleton
                            className={cn(
                              'h-5 w-5 rounded',
                              useDarkText ? 'bg-black/10' : 'bg-white/10',
                            )}
                          />
                          <Skeleton
                            className={cn(
                              'h-4 w-20 rounded group-data-[state=collapsed]:hidden',
                              useDarkText ? 'bg-black/10' : 'bg-white/10',
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Section 2 skeleton */}
                  <div className="pt-4">
                    <div
                      className={cn(
                        'mb-2',
                        isMobile
                          ? 'px-0'
                          : 'group-data-[state=collapsed]:hidden group-data-[state=expanded]:px-1',
                      )}
                    >
                      <Skeleton
                        className={cn(
                          'h-4 w-16 rounded',
                          useDarkText ? 'bg-black/10' : 'bg-white/10',
                        )}
                      />
                    </div>
                    <div className="space-y-1">
                      {[1, 2, 3].map(i => (
                        <div
                          key={i}
                          className={cn(
                            'flex h-8 items-center gap-2',
                            isMobile
                              ? 'px-4'
                              : 'group-data-[state=collapsed]:justify-center group-data-[state=expanded]:px-4',
                          )}
                        >
                          <Skeleton
                            className={cn(
                              'h-5 w-5 rounded',
                              useDarkText ? 'bg-black/10' : 'bg-white/10',
                            )}
                          />
                          <Skeleton
                            className={cn(
                              'h-4 w-24 rounded group-data-[state=collapsed]:hidden',
                              useDarkText ? 'bg-black/10' : 'bg-white/10',
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {Object.entries(groupedNavItems).map(
                    ([sectionKey, { title, items }], index) => {
                      const isCollapsible = true
                      const isCollapsed = isSectionCollapsed(
                        sectionKey as SectionKey,
                      )
                      const isFirstSection = index === 0

                      return (
                        <div
                          className={cn(
                            'group-data-[state=collapsed]:mb-0',
                            !isFirstSection && 'pt-4',
                          )}
                          key={sectionKey}
                        >
                          {title && (
                            <div
                              className={cn(
                                'mb-2 items-center',
                                isMobile
                                  ? 'flex'
                                  : 'group-data-[state=expanded]:flex group-data-[state=collapsed]:hidden group-data-[state=expanded]:px-1',
                              )}
                            >
                              {isCollapsible ? (
                                <button
                                  onClick={() =>
                                    toggleSection(sectionKey as SectionKey)
                                  }
                                  className={cn(
                                    'section-header !mb-0 flex w-full items-center justify-between rounded px-2.5 py-0.5',
                                    sectionLabelColorClasses,
                                    sectionLabelHoverClasses,
                                    hoverBgClasses,
                                  )}
                                >
                                  <span>{title}</span>
                                  <span>
                                    {isCollapsed ? (
                                      <CaretRightIcon className="h-3.5 w-3.5" />
                                    ) : (
                                      <CaretDownIcon className="h-3.5 w-3.5" />
                                    )}
                                  </span>
                                </button>
                              ) : (
                                <h2
                                  className={cn(
                                    'section-header !mb-0 px-2.5 py-0.5',
                                    sectionLabelColorClasses,
                                  )}
                                >
                                  {title}
                                </h2>
                              )}
                            </div>
                          )}
                          <SidebarSeparator
                            className={cn(
                              'opacity-10 group-data-[state=collapsed]:my-0.5',
                              title ? 'hidden' : 'my-0.5',
                              useDarkText ? 'bg-black/5' : 'bg-white/3',
                              isFirstSection && 'hidden',
                            )}
                          />

                          <div
                            className={cn(
                              'space-y-0.5 transition-all duration-200 ease-in-out',
                              // Allow sections to expand freely when not collapsed
                              isCollapsible && isCollapsed
                                ? 'max-h-0 overflow-hidden py-0 opacity-0'
                                : 'max-h-[2000px] opacity-100 group-data-[state=collapsed]:py-0 group-data-[state=expanded]:py-0',
                              // Add width constraints to prevent overflow
                              'w-full max-w-full overflow-hidden',
                            )}
                          >
                            {(() => {
                              // Pinned collections are treated as regular nav
                              // items (after the built-in ones) so they share the
                              // same show/hide, "More", muting and search logic.
                              const pinnedAsNavItems: NavItem[] =
                                getPinnedCollectionsForSection(
                                  sectionKey as SectionKey,
                                ).map(pinned => ({
                                  href: `/collections/${pinned.slug}`,
                                  label: pinned.name,
                                  icon: getCollectionIcon(pinned.icon),
                                }))
                              const combinedItems: NavItem[] = [
                                ...items,
                                ...pinnedAsNavItems,
                              ]

                              // Split items into visible and hidden. A stored
                              // per-user override wins over the item's built-in
                              // default so members can tailor their own sidebar.
                              const visibleItems = combinedItems.filter(
                                item =>
                                  !isNavItemHidden(item.href, item.hidden),
                              )
                              const hiddenItems = combinedItems.filter(item =>
                                isNavItemHidden(item.href, item.hidden),
                              )
                              const isMoreExpanded =
                                expandedMoreSections.has(sectionKey)
                              // Keep a hidden item visible inline while its page
                              // is active — but only for items hidden by default.
                              // When the user has *explicitly* hidden an item we
                              // honour that and let it move under "More" right
                              // away, so the hide action gives immediate feedback.
                              const activeHiddenItems = hiddenItems.filter(
                                item =>
                                  isLinkActive(item.href) &&
                                  effectiveHiddenNavItems[item.href] !== true,
                              )
                              const hasHiddenItems = hiddenItems.length > 0

                              const renderNavItem = (
                                item: NavItem,
                                isItemHidden = false,
                              ) => {
                                const isThreadsItem = item.href === '/threads'
                                const isTicketsItem = item.href === '/tickets'
                                const subscriptionFeature =
                                  item.subscriptionFeature
                                const unreadBadgeCount = isThreadsItem
                                  ? unreadThreadsCount
                                  : isTicketsItem
                                    ? unreadTicketsCount
                                    : undefined
                                const showUnreadBadge =
                                  typeof unreadBadgeCount === 'number' &&
                                  unreadBadgeCount > 0
                                const isSubscriptionLocked = subscriptionFeature
                                  ? !isLoadingSubscriptionFeatures &&
                                    !checkFeature(subscriptionFeature)
                                  : false
                                const subscriptionBadge = subscriptionFeature
                                  ? (SUBSCRIPTION_NAV_BADGES[
                                      subscriptionFeature
                                    ] ?? 'Pro')
                                  : 'Pro'

                                return (
                                  <SidebarMenuItem
                                    key={item.href}
                                    className="group/navitem flex justify-center group-data-[state=collapsed]:mb-0"
                                  >
                                    <SidebarMenuButton
                                      asChild
                                      isActive={isLinkActive(item.href)}
                                      tooltip={item.label}
                                      size="sm"
                                      className={cn(
                                        'h-8 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                                        textClasses,
                                        hoverBgClasses,
                                        activeBgClasses,
                                        isMobile && 'w-full px-4',
                                      )}
                                    >
                                      <Link
                                        to={getNavigationPath(item.href)}
                                        className="relative flex items-center gap-2"
                                        onClick={handleNavigate}
                                        onMouseEnter={() =>
                                          handlePrefetch(item.href)
                                        }
                                        onFocus={() =>
                                          handlePrefetch(item.href)
                                        }
                                      >
                                        <div
                                          className={cn(
                                            'relative flex h-5 w-5 shrink-0 items-center justify-center',
                                            // Mute items that live under "More"
                                            // so the split from always-shown
                                            // items reads at a glance — but keep
                                            // the active item at full strength.
                                            isItemHidden &&
                                              !isLinkActive(item.href) &&
                                              'opacity-60',
                                          )}
                                        >
                                          <item.icon className="h-5 w-5" />
                                          {showUnreadBadge &&
                                            isSidebarCollapsed && (
                                              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-blue-500" />
                                            )}
                                          {isSubscriptionLocked &&
                                            isSidebarCollapsed && (
                                              <span className="absolute -bottom-1 -right-1 flex h-3 min-w-3 items-center justify-center rounded-full bg-amber-300 px-0.5 text-[7px] font-bold leading-none text-amber-950 ring-1 ring-black/10">
                                                {subscriptionBadge.charAt(0)}
                                              </span>
                                            )}
                                        </div>
                                        <span
                                          className={cn(
                                            'flex-1 truncate text-sm group-data-[state=collapsed]:hidden',
                                            isLinkActive(item.href)
                                              ? 'font-[550]'
                                              : 'font-[450]',
                                            isItemHidden &&
                                              !isLinkActive(item.href) &&
                                              'opacity-60',
                                          )}
                                        >
                                          {item.label}
                                        </span>
                                        {isSubscriptionLocked &&
                                          !isSidebarCollapsed && (
                                            <span className="rounded-full bg-amber-300/95 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-amber-950 shadow-sm ring-1 ring-black/10 group-data-[state=collapsed]:hidden">
                                              {subscriptionBadge}
                                            </span>
                                          )}
                                        {showUnreadBadge &&
                                          !isSidebarCollapsed && (
                                            <span
                                              className={cn(
                                                'min-w-[18px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold group-data-[state=collapsed]:hidden',
                                                'bg-blue-500 text-white',
                                              )}
                                            >
                                              {unreadBadgeCount > 99
                                                ? '99+'
                                                : unreadBadgeCount}
                                            </span>
                                          )}
                                        {!isSidebarCollapsed && (
                                          // Hover actions float over the right
                                          // edge of the row (absolute, out of
                                          // flow) so they never push or wrap the
                                          // label. A blurred, sidebar-tinted
                                          // backdrop keeps the icons legible over
                                          // any text they overlap.
                                          <div
                                            className={cn(
                                              'absolute right-1 top-1/2 grid h-7 -translate-y-1/2 place-items-center gap-0.5 rounded-lg px-1 opacity-0 transition-opacity',
                                              showFocusWindowButton
                                                ? 'w-[3.875rem] grid-cols-2'
                                                : 'w-9 grid-cols-1',
                                              'bg-sidebar/80 backdrop-blur-sm',
                                              'focus-within:opacity-100 group-hover/navitem:opacity-100',
                                              'group-data-[state=collapsed]:hidden',
                                            )}
                                          >
                                            {showFocusWindowButton && (
                                              <button
                                                onClick={e =>
                                                  handleOpenFocusWindow(
                                                    e,
                                                    item.href,
                                                    item.label,
                                                  )
                                                }
                                                className="focus-visible:ring-sidebar-ring grid h-6 w-6 place-items-center rounded-md p-0 opacity-80 transition hover:bg-white/20 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1"
                                                title="Open in Focus Window"
                                              >
                                                <ArrowSquareOutIcon className="block h-3.5 w-3.5" />
                                              </button>
                                            )}
                                            <button
                                              onClick={e =>
                                                handleToggleNavVisibility(
                                                  e,
                                                  item.href,
                                                  !isItemHidden,
                                                )
                                              }
                                              title={
                                                isItemHidden
                                                  ? 'Always show in sidebar'
                                                  : 'Move under More'
                                              }
                                              aria-label={
                                                isItemHidden
                                                  ? `Always show ${item.label} in sidebar`
                                                  : `Move ${item.label} under More`
                                              }
                                              className="focus-visible:ring-sidebar-ring grid h-6 w-6 place-items-center rounded-md p-0 opacity-80 transition hover:bg-white/20 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1"
                                            >
                                              {isItemHidden ? (
                                                <EyeIcon className="block h-3.5 w-3.5" />
                                              ) : (
                                                <EyeSlashIcon className="block h-3.5 w-3.5" />
                                              )}
                                            </button>
                                          </div>
                                        )}
                                      </Link>
                                    </SidebarMenuButton>
                                  </SidebarMenuItem>
                                )
                              }

                              return (
                                <>
                                  {visibleItems.map(item =>
                                    renderNavItem(item, false),
                                  )}
                                  {/* Show active hidden items inline (when not expanded) */}
                                  {!isMoreExpanded &&
                                    activeHiddenItems.map(item =>
                                      renderNavItem(item, true),
                                    )}
                                  {/* Expanded hidden items */}
                                  {isMoreExpanded &&
                                    hiddenItems.map(item =>
                                      renderNavItem(item, true),
                                    )}
                                  {/* More toggle button */}
                                  {hasHiddenItems && (
                                    <SidebarMenuItem className="group/navitem flex justify-center group-data-[state=collapsed]:mb-0">
                                      <SidebarMenuButton
                                        tooltip={
                                          isMoreExpanded ? 'Show less' : 'More'
                                        }
                                        size="sm"
                                        className={cn(
                                          'h-6 opacity-60 transition-opacity hover:opacity-100 group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                                          textMutedClasses,
                                          hoverBgClasses,
                                          isMobile && 'w-full px-4',
                                        )}
                                        onClick={() => {
                                          setExpandedMoreSections(prev => {
                                            const next = new Set(prev)
                                            if (next.has(sectionKey)) {
                                              next.delete(sectionKey)
                                            } else {
                                              next.add(sectionKey)
                                            }
                                            return next
                                          })
                                        }}
                                      >
                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                                          <DotsThreeIcon
                                            className="h-4 w-4"
                                            weight="bold"
                                          />
                                        </div>
                                        <span className="flex-1 text-xs font-[450] group-data-[state=collapsed]:hidden">
                                          {isMoreExpanded ? 'Less' : 'More'}
                                        </span>
                                      </SidebarMenuButton>
                                    </SidebarMenuItem>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        </div>
                      )
                    },
                  )}

                  {/* Admin Panel button for mobile */}
                  {isMobile && hasAdminPermissions && (
                    <div
                      className={cn(
                        'mt-6 space-y-1 border-t pt-4',
                        borderClasses,
                      )}
                    >
                      <SidebarMenuItem className="flex justify-center">
                        <SidebarMenuButton
                          tooltip="Admin Panel"
                          size="sm"
                          onClick={toggleAdminMode}
                          className={cn(
                            'h-8 w-full px-4 text-left',
                            textClasses,
                            hoverBgClasses,
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <GearIcon className="h-5 w-5" />
                            <span className="text-sm font-[450]">
                              Admin Panel
                            </span>
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </div>
                  )}
                </>
              )}
            </SidebarMenu>
          </div>
        </SidebarContent>

        {/* Sidebar Footer */}
        <SidebarFooter
          className={cn(
            'bg-sidebar mt-auto flex-shrink-0 pb-4 pt-2 transition-colors duration-300',
            isMobile ? 'px-4' : 'px-3',
            isAdminMode && 'hidden',
          )}
        >
          <div className="space-y-1">
            <SidebarMenuItem className="flex justify-center group-data-[state=collapsed]:mb-0">
              <SidebarMenuButton
                isActive={isLinkActive('/help')}
                tooltip="Help Center"
                size="sm"
                onClick={handleOpenHelpCenter}
                onMouseEnter={() => handlePrefetch('/help')}
                onFocus={() => handlePrefetch('/help')}
                className={cn(
                  'h-8 w-full group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                  textClasses,
                  hoverBgClasses,
                  activeBgClasses,
                  isMobile && 'px-4',
                )}
              >
                <div className="flex items-center gap-2">
                  <BookOpenTextIcon className="h-5 w-5" />
                  <span
                    className={cn(
                      'text-sm group-data-[state=collapsed]:hidden',
                      isLinkActive('/help') ? 'font-[550]' : 'font-[450]',
                    )}
                  >
                    Help Center
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {/* Invite Team Members Button */}
            {hasAdminPermissions && (
              <SidebarMenuItem className="flex justify-center group-data-[state=collapsed]:mb-0">
                <SidebarMenuButton
                  tooltip={isPersonal ? 'Invite family' : 'Invite Team Members'}
                  size="sm"
                  onClick={() => setShowInviteDialog(true)}
                  className={cn(
                    'h-8 w-full group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                    textClasses,
                    hoverBgClasses,
                    isMobile && 'px-4',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <UserPlusIcon className="h-5 w-5" />
                    <span className="text-sm font-[450] group-data-[state=collapsed]:hidden">
                      {isPersonal ? 'Invite family' : 'Invite Team Members'}
                    </span>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {/* Admin Panel Button */}
            {hasAdminPermissions && (
              <SidebarMenuItem className="flex justify-center group-data-[state=collapsed]:mb-0">
                <SidebarMenuButton
                  tooltip="Admin Panel"
                  size="sm"
                  onClick={toggleAdminMode}
                  className={cn(
                    'h-8 w-full group-data-[state=expanded]:px-4 group-data-[state=expanded]:text-left',
                    textClasses,
                    hoverBgClasses,
                    isMobile && 'px-4',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <GearIcon className="h-5 w-5" />
                    <span className="text-sm font-[450] group-data-[state=collapsed]:hidden">
                      Admin Panel
                    </span>
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </div>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset
        className={cn(
          'app-shell-inset flex h-full min-h-0 w-full min-w-0 flex-col',
          isDesktopTauri
            ? tauriGlassEnabled
              ? 'bg-transparent'
              : 'bg-background'
            : 'bg-background',
          !isMobile && 'mb-4 ml-0',
          !isMobile && !isDesktopTauri && 'shadow-sm',
        )}
        tauriOffset={isTauri() && !isMobile}
      >
        <Header />
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
          {isDesktopTauri && (
            <div
              aria-hidden
              className={cn(
                'app-shell-corner-notch pointer-events-none absolute left-0 top-0 z-0 h-4 w-4',
                tauriGlassEnabled ? 'tauri-glass-corner-fill' : 'bg-sidebar',
              )}
            />
          )}
          <div
            className={cn(
              'app-shell-content-surface relative z-10 h-full min-h-0 min-w-0 overflow-hidden rounded-tl-2xl',
            )}
          >
            {children}
          </div>
        </div>
      </SidebarInset>

      {/* Invite Team Members Dialog */}
      <InviteTeamMembersDialog
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSuccess={() => {
          setShowInviteDialog(false)
        }}
      />
    </div>
  )
}

export { CustomSidebarTrigger }
